import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import { Quote } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const stats = [
  { value: "1,000+", key: "s1" },
  { value: "6+", key: "s2" },
  { value: "24/7", key: "s3" },
  { value: "Custom", key: "s4" },
];

const testimonials = [
  { quote: "Their custom support agent now handles the bulk of our inbound questions — our team finally focuses on the hard cases.", role: "Head of Support", org: "SaaS company" },
  { quote: "The recruitment agent screens and schedules for us. We fill roles noticeably faster with far less manual work.", role: "Talent Lead", org: "Scale-up" },
  { quote: "We described our workflow and they built an agent around it. It just fits how we already work.", role: "Operations Manager", org: "Logistics firm" },
];

// Unbranded placeholder marks — swap for real client logos when available.
const placeholders = ["Acme Co.", "Northwind", "Globex", "Initech", "Umbra", "Vertex"];

export default function TrustStrip({ showTestimonials = true }) {
  const { t } = useLanguage();
  return (
    <section className="py-24 border-t border-white/8 bg-[#07111F]/40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionLabel>{t("trust.label")}</SectionLabel>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
            {t("trust.h2")}
          </h2>
        </Reveal>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Reveal key={s.key} delay={(i % 4) * 0.06}>
              <div className="vora-card rounded-2xl p-6 text-center" data-testid={`trust-stat-${i + 1}`}>
                <p className="text-3xl sm:text-4xl font-bold tracking-tight vora-text-gradient">{s.value}</p>
                <p className="mt-2 text-sm text-slate-400">{t(`trust.${s.key}`)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Placeholder logo strip */}
        <Reveal delay={0.1}>
          <div className="mt-10">
            <p className="text-center text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">{t("trust.logosCaption")}</p>
            <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="trust-logos">
              {placeholders.map((p) => (
                <div key={p} className="flex h-14 items-center justify-center rounded-xl border border-white/8 bg-white/[0.02] text-sm font-semibold text-slate-500">
                  {p}
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-xs text-slate-600">{t("trust.logosNote")}</p>
          </div>
        </Reveal>

        {/* Testimonials */}
        {showTestimonials && (
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {testimonials.map((t2, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full vora-card rounded-2xl p-6" data-testid={`trust-testimonial-${i + 1}`}>
                  <Quote className="h-6 w-6 text-[#4F8CFF]/60" />
                  <p className="mt-4 text-sm text-slate-200 leading-relaxed">"{t2.quote}"</p>
                  <p className="mt-5 text-xs text-slate-400">{t2.role} · <span className="text-slate-500">{t2.org}</span></p>
                </div>
              </Reveal>
            ))}
            <p className="md:col-span-3 text-center text-xs text-slate-600">{t("trust.testiNote")}</p>
          </div>
        )}
      </div>
    </section>
  );
}
